import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { saleForCatNo } from '../utils/productSale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// USPS Shipping Options — visible upfront pricing so customers know
// shipping cost during checkout. Admin may still fine-tune the final
// shipping cost on the invoice for unusual weights / destinations.
// Free shipping on orders ≥ FREE_SHIPPING_THRESHOLD applies ONLY to
// USPS Ground Advantage. Priority and Express tiers always charge
// their listed fee regardless of order size.
export const FREE_SHIPPING_THRESHOLD = 300;
export const FREE_SHIPPING_METHOD_ID = 'ground';

// Returns true when an order with the given shipping option qualifies for
// the $300+ free-shipping promotion. Single source of truth — also used by
// CartSidebar, Checkout, and the FE display logic.
export const isFreeShippingEligible = (shippingOptionId, subtotal) =>
  shippingOptionId === FREE_SHIPPING_METHOD_ID && subtotal >= FREE_SHIPPING_THRESHOLD;

export const SHIPPING_OPTIONS = {
  ground:    { id: 'ground',    name: 'USPS Ground Advantage',          price: 15.00, quoted: false, days: '3-5 business days' },
  priority:  { id: 'priority',  name: 'USPS Priority Mail',             price: 25.00, quoted: false, days: '2-3 business days' },
  express:   { id: 'express',   name: 'USPS Priority Express',          price: 53.34, quoted: false, days: '1-2 business days' },
  pickup:    { id: 'pickup',    name: 'Local Pickup (Livingston, TX)',  price: 0,     quoted: false, days: 'Same day' }
};

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Tracks the last time saveCartToServer completed successfully — drives
  // the brief "Synced ✓" pulse next to the cart icon so users can see
  // their changes were persisted (and notice if they aren't).
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [shippingOption, setShippingOption] = useState('ground'); // Default to free Ground Advantage

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('rcm_cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('rcm_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Save cart to server (for logged in users)
  // We DELIBERATELY allow empty carts to be saved — otherwise "clear cart"
  // never reaches the server and the stale items reappear the next time
  // the user logs in (the loadCartFromServer merge would resurrect them).
  const saveCartToServer = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    setIsSaving(true);
    try {
      await fetch(`${API_URL}/api/cart/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items: cartItems })
      });
      setLastSavedAt(Date.now());
    } catch (error) {
      console.error('Failed to save cart to server:', error);
    } finally {
      setIsSaving(false);
    }
  }, [cartItems]);

  // Load cart from server (for logged in users)
  const loadCartFromServer = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/cart/load`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          // Merge server cart with local cart
          setCartItems(prevItems => {
            const serverItems = data.items;
            const localIds = prevItems.map(i => i.id);
            const newItems = serverItems.filter(i => !localIds.includes(i.id));
            return [...prevItems, ...newItems];
          });
        }
      }
    } catch (error) {
      console.error('Failed to load cart from server:', error);
    }
  }, []);

  // Auto-save to server when cart changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      saveCartToServer();
    }, 2000); // Save after 2 seconds of no changes

    return () => clearTimeout(timer);
  }, [cartItems, saveCartToServer]);

  // Timed sales discount the single-vial price only; the server re-checks every price at checkout.
  const getEffectivePrice = (product, packType) => {
    if (packType === '10pack') return product.price10 || 0;
    const sale = saleForCatNo(product, product.catNo);
    return sale.active ? sale.salePrice : (product.price1 || 0);
  };

  // Re-read authoritative prices so carts opened before a sale started/ended show what will be charged.
  const cartRef = useRef(cartItems);
  cartRef.current = cartItems;
  const [nextPriceChange, setNextPriceChange] = useState(null);
  const repriceCart = useCallback(async ({ silent = false } = {}) => {
    const items = cartRef.current;
    if (!items.length) return { changed: [] };
    try {
      const res = await fetch(`${API_URL}/api/cart/reprice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items.map((i) => ({ catNo: i.catNo, pack_type: i.packType })) }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      const key = (c, p) => `${c}|${p}`;
      const fresh = new Map(data.items.map((x) => [key(x.catNo, x.pack_type), x]));
      const changed = items
        .map((i) => ({ item: i, hit: fresh.get(key(i.catNo, i.packType)) }))
        .filter(({ item, hit }) => hit && hit.unit_price != null && Math.abs(hit.unit_price - item.price) > 0.004)
        .map(({ item, hit }) => ({ id: item.id, name: `${item.name} ${item.dosage || ''}`.trim(), from: item.price, to: hit.unit_price }));
      if (changed.length) {
        const byId = new Map(changed.map((c) => [c.id, c.to]));
        setCartItems((prev) => prev.map((i) => (byId.has(i.id) ? { ...i, price: byId.get(i.id) } : i)));
        if (!silent) {
          toast.info(`Cart prices updated: ${changed.map((c) => `${c.name} now $${c.to.toFixed(2)}`).join('; ')}`, { duration: 8000 });
        }
      }
      setNextPriceChange(data.next_change_at ? Date.parse(data.next_change_at) : null);
      return { changed };
    } catch {
      return null;
    }
  }, []);

  const cartSignature = cartItems.map((i) => `${i.catNo}|${i.packType}`).join(',');
  useEffect(() => {
    if (!cartSignature) return undefined;
    const id = setTimeout(() => repriceCart(), 400);
    return () => clearTimeout(id);
  }, [cartSignature, repriceCart]);

  useEffect(() => {
    const onFocus = () => { if (document.visibilityState !== 'hidden') repriceCart(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onFocus); };
  }, [repriceCart]);

  useEffect(() => {
    if (!nextPriceChange) return undefined;
    const id = setTimeout(() => repriceCart(), Math.min(2147483000, Math.max(0, nextPriceChange - Date.now()) + 1500));
    return () => clearTimeout(id);
  }, [nextPriceChange, repriceCart]);

  const addToCart = (product, quantity, packType) => {
    const price = getEffectivePrice(product, packType);

    // Hard guard: never accept a $0 / missing-price item into the cart.
    // (Caused at least one $0.00 invoice — see RCM-2026-00016 for the historical case.)
    if (!price || isNaN(price) || price <= 0) {
      // Lazy import of toast to avoid circular dep at module load
      try {
        // eslint-disable-next-line global-require
        const { toast } = require('sonner');
        toast.error('Please select a size / variant before adding to cart.');
      } catch {}
      // eslint-disable-next-line no-console
      console.warn('[cart] refused to add zero-price item', { catNo: product?.catNo, name: product?.name, packType });
      return;
    }

    // Track add-to-cart for the data-driven "Most Requested" homepage rail.
    // Fire-and-forget — never blocks the cart flow.
    if (product?.catNo) {
      try {
        // eslint-disable-next-line global-require
        const { trackProductEvent } = require('../utils/productEvents');
        trackProductEvent(product.catNo, 'add_to_cart');
      } catch {}
    }

    const itemId = `${product.catNo}-${packType}`;
    
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === itemId);
      let updatedItems;
      
      if (existingItem) {
        updatedItems = prevItems.map(item =>
          item.id === itemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        updatedItems = [...prevItems, {
          id: itemId,
          catNo: product.catNo,
          name: product.name,
          dosage: product.dosage,
          packType,
          packLabel: packType === '10pack' ? '10-Pack' : 'Single Vial',
          price,
          quantity,
          image: product.image
        }];
      }
      
      return updatedItems;
    });
    
    setIsCartOpen(true);
  };

  const removeFromCart = (itemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    // Eagerly clear the server-side cart so a subsequent login on another
    // device / browser doesn't resurrect the items. The 2s auto-save will
    // also fire (idempotent — both POST an empty list), but this makes the
    // server state correct immediately after the user clicks "Clear cart".
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetch(`${API_URL}/api/cart/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ items: [] })
      }).catch(err => console.error('Failed to clear server cart:', err));
    }
  };

  const getCartTotal = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    const selectedShipping = SHIPPING_OPTIONS[shippingOption] || SHIPPING_OPTIONS.ground;
    const eligible = isFreeShippingEligible(selectedShipping.id, subtotal);
    const shipping = eligible ? 0 : selectedShipping.price;

    const total = subtotal + shipping;

    return {
      subtotal,
      shipping,
      total,
      itemCount: cartItems.reduce((sum, item) => sum + item.quantity, 0),
      shippingOption: selectedShipping,
      freeShipping: eligible
    };
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      isCartOpen,
      setIsCartOpen,
      isSaving,
      lastSavedAt,
      shippingOption,
      setShippingOption,
      addToCart,
      getEffectivePrice,
      repriceCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      getCartTotal,
      toggleCart,
      saveCartToServer,
      loadCartFromServer,
      SHIPPING_OPTIONS
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
